import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  ButtonBase,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ArrowForward, PercentOutlined, DiscountOutlined } from '@mui/icons-material'
import { useIntl } from 'react-intl'

import { useBenefitsList } from 'services/benefits/benefits.hooks'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useLanguage } from 'services/i18n/i18n.hooks'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import NoActiveContractMessage from 'components/NoActiveContractMessage'

// Benefit descriptions come as HTML — strip tags for the card snippet.
const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

// Discounts grid ("Panel Klienta 3.0" Strefa rabatów tab).
const DiscountsList: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { benefitsList = [], isFetching, isLoading } = useBenefitsList()
  const { formatMessage } = useIntl()
  const { language } = useLanguage()
  const { leaseContractsList, isLoading: isLeaseLoading } = useLeaseContractsList()
  const navigate = useNavigate()

  const activeLeases = leaseContractsList.filter(({ leaseStatus }) => leaseStatus === 0)

  if (isLeaseLoading || isLoading || isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (activeLeases.length === 0) {
    return <NoActiveContractMessage />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px' }}>
        {isDesktop && (
          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: theme.r4r.navy }}>
            {formatMessage({ id: 'discounts.zone', defaultMessage: 'Strefa rabatów' })}
          </Typography>
        )}
        {!!benefitsList.length && (
          <Box
            sx={{
              padding: '4px 12px',
              backgroundColor: alpha(theme.r4r.azure, 0.15),
              color: theme.r4r.navy,
              fontSize: '12px',
              fontWeight: 700
            }}>
            {formatMessage(
              { id: 'discounts.activeCount', defaultMessage: '{count} aktywnych' },
              { count: benefitsList.length }
            )}
          </Box>
        )}
      </Box>
      {!benefitsList.length ? (
        <HelpdeskEmptyState
          Icon={DiscountOutlined}
          title={formatMessage({
            id: 'discountsList.noDiscounts',
            defaultMessage: 'Brak rabatów.'
          })}
          subtitle={formatMessage({
            id: 'discounts.noDiscountsHint',
            defaultMessage: 'Nowe oferty dla mieszkańców pojawią się tutaj.'
          })}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isDesktop ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr',
            gap: '20px'
          }}>
          {benefitsList.map(({ id, name, description, descriptionLocal, logoThumbnail, benefitCategory }) => {
            const categoryLabel =
              language === 'pl' ? benefitCategory?.namePl : benefitCategory?.nameEn
            const snippet = stripHtml(
              (language === 'pl' ? descriptionLocal : description) ||
                descriptionLocal ||
                description ||
                ''
            )
            return (
              <ButtonBase
                key={id}
                onClick={() => navigate(`/discounts/${id}`)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  backgroundColor: '#fff',
                  boxShadow: theme.r4r.cardShadow,
                  overflow: 'hidden'
                }}>
                <Box
                  sx={{
                    position: 'relative',
                    height: '140px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: !!logoThumbnail
                      ? `url("${logoThumbnail}") center / cover no-repeat`
                      : theme.r4r.gradient
                  }}>
                  {!logoThumbnail && (
                    <PercentOutlined sx={{ color: '#fff', fontSize: '40px' }} />
                  )}
                  {!!categoryLabel && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '14px',
                        bottom: '14px',
                        padding: '4px 12px',
                        backgroundColor: '#fff',
                        color: theme.r4r.navy,
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                      {categoryLabel}
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    padding: '16px 18px'
                  }}>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
                    {name}
                  </Typography>
                  {!!snippet && (
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: theme.r4r.grey,
                        mt: '6px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                      {snippet}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      mt: 'auto',
                      pt: '14px'
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '7px 14px',
                        backgroundColor: theme.r4r.navy,
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 700
                      }}>
                      {formatMessage({ id: 'discounts.use', defaultMessage: 'Skorzystaj' })}
                      <ArrowForward sx={{ fontSize: '14px' }} />
                    </Box>
                  </Box>
                </Box>
              </ButtonBase>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default DiscountsList
